import clsx from 'clsx';
import { graphql } from 'gatsby';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { Section } from '~components/Section';
import { ComponentLink, ComponentLinkProps } from '~components/ComponentLink';
import { renderHeading, renderParagraph, RichTextRenderer } from '~components/RichTextRenderer';
import { tildeToSoftHyphen } from '~utils/string';
import { renderEmbeddedBlockEntry, renderEmbeddedInlineEntry } from './renderers';
import Arrow from './arrow.svg';

import * as styles from './ComponentActivityCards.module.css';

import type { ContentBlock } from '~components/types';
import type { ContentfulComponentActivityCards } from './types';

type ComponentActivityCardsProps = {
  content: ContentfulComponentActivityCards;
};

export const ComponentActivityCards: ContentBlock<ComponentActivityCardsProps> = ({ content, ...sectionProps }) => (
  <Section {...sectionProps} blockLayout='boxed' className={styles.section}>
    {content.contentBefore && (
      <RichTextRenderer
        className={styles.richText}
        content={content.contentBefore.raw}
        wrapperElement='div'
        options={{
          renderNode: {
            [BLOCKS.HEADING_2]: renderHeading(1, 2, { className: styles.h2 }),
            [BLOCKS.HEADING_3]: renderHeading(2, 3, { className: styles.h3 }),
            [BLOCKS.HEADING_4]: renderHeading(3, 4, { className: styles.h4 }),
            [BLOCKS.PARAGRAPH]: renderParagraph({ className: styles.p }),
            [BLOCKS.EMBEDDED_ENTRY]: renderEmbeddedBlockEntry(content.contentBefore.references),
            [INLINES.EMBEDDED_ENTRY]: renderEmbeddedInlineEntry(content.contentBefore.references),
          },
        }}
      />
    )}
    <div className={styles.cardGrid}>
      {content.items.map((link) => (
        <Card key={link.contentful_id + link.label} link={link} />
      ))}
      {content.extraItem && <Card className={styles.fixed} link={content.extraItem} />}
    </div>
  </Section>
);

const Card: React.FC<ComponentLinkProps> = (props) => (
  <ComponentLink {...props} className={clsx(styles.card, props.className)}>
    <span>{tildeToSoftHyphen(props.link.label)}</span>
    <Arrow className={styles.arrowRight} />
  </ComponentLink>
);

export const query = graphql`
  fragment ComponentActivityCards on ContentfulComponentActivityCards {
    __typename
    contentBefore {
      raw
      references {
        ...ComponentLink
      }
    }
    items {
      ...ComponentLink
    }
    extraItem {
      ...ComponentLink
    }
  }
`;
